import React, { useState, useEffect, useMemo } from 'react';
import './Profile.css';
import { ReactComponent as PencilIcon } from '../../assets/images/pencil.svg';
import { getMe, updateUser } from '../../services/userService';
import {
  gradeOptions,
  getClassLevelOptionsByCountry,
  getClassLevelSystemLabelByCountry,
  getExamOptionsByCountry,
  getExamSystemLabelByCountry,
} from '../../config';
import { countryOptions } from '../../utils/countryOptions';

interface UserProfile {
  name: string;
  lastName: string;
  email: string;
  country: string;
  classLevel: string;
  board: string;
  currentGrade: string;
  gradeExpectation: string;
  weakness: string;
  strength: string;
}

const initialUser: UserProfile = {
  name: '',
  lastName: '',
  email: '',
  country: '',
  classLevel: '',
  board: '',
  currentGrade: '',
  gradeExpectation: '',
  weakness: '',
  strength: '',
};

const Profile: React.FC = () => {
  const [user, setUser] = useState<UserProfile>(initialUser);
  const [originalUser, setOriginalUser] = useState<UserProfile>(initialUser);
  const [activeTab, setActiveTab] = useState('personal');
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Restored State Logic
  const [customFields, setCustomFields] = useState({
    country: '',
    classLevel: '',
  });
  const [gradeMode, setGradeMode] = useState<'grade' | 'percentage' | 'performance'>('grade');
  const [levelType, setLevelType] = useState<'className' | 'examName'>('className');

  // Email specific state
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');
  const [showCustomCountryInput, setShowCustomCountryInput] = useState(false);
  const [showCustomClassLevelInput, setShowCustomClassLevelInput] = useState(false);
  const availableClassLevelOptions = useMemo(
    () => getClassLevelOptionsByCountry(user.country),
    [user.country],
  );
  const availableExamOptions = useMemo(
    () => getExamOptionsByCountry(user.country),
    [user.country],
  );
  const isCountrySelected = Boolean(user.country.trim());
  const isProfileEducationSelectionDisabled = !isCountrySelected;
  const activeEducationOptions =
    levelType === 'className' ? availableClassLevelOptions : availableExamOptions;
  const selectedCountryValue = showCustomCountryInput
    ? 'Other'
    : user.country;
  const selectedClassLevelValue = showCustomClassLevelInput
    ? 'Other'
    : user.classLevel;

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await getMe();
        setUser(userData);
        setOriginalUser(userData);

        // Initialize Logic from Old Profile
        if (userData.currentGrade) {
          const grade = parseFloat(userData.currentGrade);
          if (grade <= 5 && !gradeOptions.includes(userData.currentGrade)) {
            setGradeMode('performance');
          } else if (grade <= 100 && !gradeOptions.includes(userData.currentGrade)) {
            setGradeMode('percentage');
          } else {
            setGradeMode('grade');
          }
        }

        if (userData.classLevel) {
          const userClassLevelOptions = getClassLevelOptionsByCountry(userData.country);
          const userExamOptions = getExamOptionsByCountry(userData.country);
          if (userClassLevelOptions.includes(userData.classLevel)) {
            setLevelType('className');
            setShowCustomClassLevelInput(false);
          } else if (userExamOptions.includes(userData.classLevel)) {
            setLevelType('examName');
            setShowCustomClassLevelInput(false);
          } else {
            setLevelType(userExamOptions.includes(userData.classLevel) ? 'examName' : 'className');
            setShowCustomClassLevelInput(true);
            setCustomFields(prev => ({ ...prev, classLevel: userData.classLevel }));
          }
        }

        setShowCustomCountryInput(Boolean(userData.country) && !countryOptions.includes(userData.country));

        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setIsLoading(false);
      }
    };
    fetchUserData();
  }, []);

  const handleInputChange = (field: keyof UserProfile, value: string) => {
    setUser({ ...user, [field]: value });
  };

  const updateCountrySelection = (nextCountry: string) => {
    const currentOptions =
      levelType === 'className'
        ? getClassLevelOptionsByCountry(user.country)
        : getExamOptionsByCountry(user.country);
    const nextOptions =
      levelType === 'className'
        ? getClassLevelOptionsByCountry(nextCountry)
        : getExamOptionsByCountry(nextCountry);
    const shouldResetSelection =
      Boolean(user.classLevel) &&
      currentOptions.includes(user.classLevel) &&
      !nextOptions.includes(user.classLevel);

    setUser((prev) => ({
      ...prev,
      country: nextCountry,
      classLevel: shouldResetSelection ? '' : prev.classLevel,
    }));

    if (shouldResetSelection) {
      setShowCustomClassLevelInput(false);
      setCustomFields((prev) => ({ ...prev, classLevel: '' }));
    }
  };

  const handleUpdate = async () => {
    setIsSaving(true);
    try {
      await updateUser(user);
      setOriginalUser(user);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setUser(originalUser);
    setCustomFields(prev => ({
      ...prev,
      country: originalUser.country,
      classLevel: originalUser.classLevel,
    }));
    setLevelType(
      getClassLevelOptionsByCountry(originalUser.country).includes(originalUser.classLevel)
        ? 'className'
        : getExamOptionsByCountry(originalUser.country).includes(originalUser.classLevel)
          ? 'examName'
          : 'className'
    );
    setShowCustomCountryInput(Boolean(originalUser.country) && !countryOptions.includes(originalUser.country));
    setShowCustomClassLevelInput(
      Boolean(originalUser.classLevel) &&
        !getClassLevelOptionsByCountry(originalUser.country).includes(originalUser.classLevel) &&
        !getExamOptionsByCountry(originalUser.country).includes(originalUser.classLevel)
    );
    setIsEditing(false);
  };

  // --- Email Logic ---
  const handleSendOtp = async () => {
    if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setOtpError('Please enter a valid email');
      return;
    }
    setIsSendingOtp(true);
    setOtpError('');
    setOtpSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/send-email-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ newEmail })
      });
      const data = await response.json();
      if (data.status) {
        setIsOtpSent(true);
        setOtpSuccess('OTP sent to your new email!');
      } else {
        setOtpError(data.msg || 'Failed to send OTP');
      }
    } catch (error) {
      setOtpError('Failed to send OTP. Please try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!emailOtp || emailOtp.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/verify-email-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ newEmail, otp: emailOtp })
      });

      const data = await response.json();

      if (data.status) {
        setUser(prev => ({ ...prev, email: newEmail }));
        setOriginalUser(prev => ({ ...prev, email: newEmail }));
        setOtpSuccess('Email updated successfully!');
        setTimeout(() => {
          setIsEditingEmail(false);
          setIsOtpSent(false);
          setEmailOtp('');
          setNewEmail('');
          setOtpSuccess('');
        }, 2000);
      } else {
        setOtpError(data.msg || 'Invalid OTP');
      }
    } catch (error) {
      setOtpError('Failed to verify OTP. Please try again.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleCancelEmailEdit = () => {
    setIsEditingEmail(false);
    setIsOtpSent(false);
    setEmailOtp('');
    setNewEmail('');
    setOtpError('');
    setOtpSuccess('');
  };

  const SidebarItem = ({ id, label }: { id: string, label: string }) => (
    <div
      className={`sidebar-item ${activeTab === id ? 'active' : ''}`}
      onClick={() => setActiveTab(id)}
    >
      <span>{label}</span>
    </div>
  );

  if (isLoading) return <div className="loading-container">Loading...</div>;

  return (
    <div className="profile-page-container">
      {/* Header */}
      <div className="profile-header-new">
        <div className="user-summary">
          <div className="avatar-circle">
            {user.name?.[0]}{user.lastName?.[0]}
            {/* Removed Edit Avatar btn as per old profile */}
          </div>
          <div className="user-details-summary">
            <h2>{user.name} {user.lastName}</h2>
            <p className="user-phone">{user.email}</p>
          </div>
        </div>

        <div className="header-stats">
          <div className="stat-item">
            <span className="stat-label">Class</span>
            <span className="stat-value">{user.classLevel || '-'}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Board</span>
            <span className="stat-value">{user.board || '-'}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Target</span>
            <span className="stat-value">Class/Sc...</span>
          </div>
          {/* Change button just triggers edit mode on academic tab? Or link? keeping simple for now */}
          <button className="change-btn" onClick={() => { setActiveTab('academic'); setIsEditing(true); }}>Change</button>
        </div>
      </div>

      <div className="profile-content-layout">
        {/* Sidebar */}
        <div className="profile-sidebar">
          <SidebarItem id="personal" label="Personal Details" />
          <SidebarItem id="academic" label="Academic Details" />
          <SidebarItem id="security" label="Sign-in & Security" />
          {/* Removed unrelated tabs to match "fields pahle wale hi honge" request scope */}
        </div>

        {/* Main Form Area */}
        <div className="profile-main-form">

          {/* Personal Details */}
          {activeTab === 'personal' && (
            <div className="form-section">
              <h3 className="section-title">Personal Details</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>First name</label>
                  <input
                    type="text"
                    value={user.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label>Last name</label>
                  <input
                    type="text"
                    value={user.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    disabled={!isEditing}
                  />
                </div>

                {/* Country with Custom Logic */}
                <div className="form-group">
                  <label>Country</label>
                  <select
                    value={selectedCountryValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'Other') {
                        setShowCustomCountryInput(true);
                        updateCountrySelection(customFields.country);
                      } else {
                        setShowCustomCountryInput(false);
                        setCustomFields({ ...customFields, country: '' });
                        updateCountrySelection(value);
                      }
                    }}
                    disabled={!isEditing}
                  >
                    <option value="">Select Country</option>
                    {countryOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    <option value="Other">Other</option>
                  </select>
                </div>
                {showCustomCountryInput && (
                  <div className="form-group">
                    <label>Enter Country</label>
                    <input
                      type="text"
                      value={customFields.country || user.country}
                      onChange={(e) => {
                        setCustomFields({ ...customFields, country: e.target.value });
                        updateCountrySelection(e.target.value);
                      }}
                      disabled={!isEditing}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Academic Details (Combined Education + Academic Profile) */}
          {activeTab === 'academic' && (
            <div className="form-section">
              <h3 className="section-title">Education</h3>

              {/* Class/Exam Toggle */}
              {isEditing && (
                <div className="radio-group" style={{ marginBottom: '16px' }}>
                  <label className={`radio-label ${levelType === 'className' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      checked={levelType === 'className'}
                      onChange={() => {
                        setLevelType('className');
                        // Reset if switching types
                        if (availableExamOptions.includes(user.classLevel)) {
                          setUser({ ...user, classLevel: '' });
                        }
                      }}
                    /> Class Name
                  </label>
                  <label className={`radio-label ${levelType === 'examName' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      checked={levelType === 'examName'}
                      onChange={() => {
                        setLevelType('examName');
                        if (availableClassLevelOptions.includes(user.classLevel)) {
                          setUser({ ...user, classLevel: '' });
                        }
                      }}
                    /> Exam Name
                  </label>
                </div>
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label>{levelType === 'className' ? 'Class Level' : 'Exam Name'}</label>
                  <select
                    value={selectedClassLevelValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'Other') {
                        setShowCustomClassLevelInput(true);
                        setUser({ ...user, classLevel: customFields.classLevel });
                      } else {
                        setShowCustomClassLevelInput(false);
                        setUser({ ...user, classLevel: value });
                        setCustomFields({ ...customFields, classLevel: '' });
                      }
                    }}
                    disabled={!isEditing || isProfileEducationSelectionDisabled}
                  >
                    <option value="">Select {levelType === 'className' ? 'Class' : 'Exam'}</option>
                    {activeEducationOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    <option value="Other">Other</option>
                  </select>
                  <span className="field-hint">
                    {isProfileEducationSelectionDisabled
                      ? 'Select a country first to load the correct list.'
                      : levelType === 'className'
                        ? `${getClassLevelSystemLabelByCountry(user.country)} loaded for ${user.country}.`
                        : `${getExamSystemLabelByCountry(user.country)} loaded for ${user.country}.`}
                  </span>
                </div>

                {/* Custom Class Level Field */}
                {showCustomClassLevelInput && (
                  <div className="form-group">
                    <label>Enter {levelType === 'className' ? 'Class' : 'Exam'}</label>
                    <input
                      type="text"
                      value={customFields.classLevel || user.classLevel}
                      onChange={(e) => {
                        setCustomFields({ ...customFields, classLevel: e.target.value });
                        setUser({ ...user, classLevel: e.target.value });
                      }}
                      disabled={!isEditing || isProfileEducationSelectionDisabled}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Board</label>
                  <input type="text" value={user.board} onChange={(e) => handleInputChange('board', e.target.value)} disabled={!isEditing} />
                </div>
              </div>

              <h3 className="section-title" style={{ marginTop: '32px' }}>Academic Profile</h3>

              {/* Grade Mode Toggle */}
              {isEditing && (
                <div className="grade-toggle" style={{ marginBottom: '20px' }}>
                  <label className="radio-label">
                    <input type="radio" checked={gradeMode === 'grade'} onChange={() => { setGradeMode('grade'); setUser({ ...user, currentGrade: '', gradeExpectation: '' }); }} />
                    Grade
                  </label>
                  <label className="radio-label">
                    <input type="radio" checked={gradeMode === 'percentage'} onChange={() => { setGradeMode('percentage'); setUser({ ...user, currentGrade: '', gradeExpectation: '' }); }} />
                    Percentage
                  </label>
                  <label className="radio-label">
                    <input type="radio" checked={gradeMode === 'performance'} onChange={() => { setGradeMode('performance'); setUser({ ...user, currentGrade: '', gradeExpectation: '' }); }} />
                    Performance
                  </label>
                </div>
              )}

              <div className="form-grid">
                {/* Current Grade */}
                <div className="form-group">
                  <label>Current Grade {gradeMode !== 'grade' && (gradeMode === 'percentage' ? '(1-100)' : '(1-5)')}</label>
                  {gradeMode === 'grade' ? (
                    <select value={user.currentGrade} onChange={(e) => handleInputChange('currentGrade', e.target.value)} disabled={!isEditing}>
                      <option value="">Select Grade</option>
                      {gradeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : (
                    <input
                      type="number"
                      value={user.currentGrade}
                      onChange={(e) => handleInputChange('currentGrade', e.target.value)}
                      disabled={!isEditing}
                      min={1}
                      max={gradeMode === 'percentage' ? 100 : 5}
                    />
                  )}
                </div>

                {/* Expected Grade */}
                <div className="form-group">
                  <label>Expected Grade</label>
                  {gradeMode === 'grade' ? (
                    <select value={user.gradeExpectation} onChange={(e) => handleInputChange('gradeExpectation', e.target.value)} disabled={!isEditing}>
                      <option value="">Select Grade</option>
                      {gradeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : (
                    <input
                      type="number"
                      value={user.gradeExpectation}
                      onChange={(e) => handleInputChange('gradeExpectation', e.target.value)}
                      disabled={!isEditing}
                      min={1}
                      max={gradeMode === 'percentage' ? 100 : 5}
                    />
                  )}
                </div>

                <div className="form-group">
                  <label>Strength</label>
                  <input type="text" value={user.strength} onChange={(e) => handleInputChange('strength', e.target.value)} disabled={!isEditing} />
                </div>
                <div className="form-group">
                  <label>Weakness</label>
                  <input type="text" value={user.weakness} onChange={(e) => handleInputChange('weakness', e.target.value)} disabled={!isEditing} />
                </div>
              </div>

            </div>
          )}

          {/* Security (Email OTP) */}
          {activeTab === 'security' && (
            <div className="form-section">
              <h3 className="section-title">Sign-in & Security</h3>

              {!isEditingEmail ? (
                <div className="form-grid">
                  <div className="form-group">
                    <label>Email Address</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input type="email" value={user.email} disabled style={{ flex: 1 }} />
                      <button className="btn-secondary" onClick={() => setIsEditingEmail(true)} style={{ padding: '0 20px' }}><PencilIcon style={{ width: 16 }} /></button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="email-edit-container" style={{ maxWidth: '400px' }}>
                  {!isOtpSent ? (
                    <div className="form-group">
                      <label>Enter New Email</label>
                      <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="New Email Address" />
                      <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                        <button className="btn-primary" onClick={handleSendOtp} disabled={isSendingOtp}>
                          {isSendingOtp ? 'Sending...' : 'Send OTP'}
                        </button>
                        <button className="btn-secondary" onClick={handleCancelEmailEdit}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>Enter OTP</label>
                      <input type="text" value={emailOtp} onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6-digit OTP" maxLength={6} />
                      <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                        <button className="btn-primary" onClick={handleVerifyOtp} disabled={isVerifyingOtp}>
                          {isVerifyingOtp ? 'Verifying...' : 'Verify OTP'}
                        </button>
                        <button className="btn-secondary" onClick={handleCancelEmailEdit}>Cancel</button>
                      </div>
                    </div>
                  )}
                  {otpError && <p className="otp-error">{otpError}</p>}
                  {otpSuccess && <p className="otp-success">{otpSuccess}</p>}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons for Main Forms */}
          {activeTab !== 'security' && (
            <div className="form-actions">
              {!isEditing ? (
                <button className="btn-primary" onClick={() => setIsEditing(true)}>Edit Profile</button>
              ) : (
                <>
                  <button className="btn-secondary" onClick={handleCancel}>Cancel</button>
                  <button className="btn-primary" onClick={handleUpdate} disabled={isSaving}>
                    {isSaving ? 'Updating...' : 'Update'}
                  </button>
                </>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Profile;
